import React, { useMemo, useState } from 'react';
import { X, ChevronRight, ChevronLeft, CheckCircle, Repeat } from 'lucide-react';
import { Job, JobHistoryEntry, JobRecurrenceRule, JobStatus, Site } from '../../types/site';
import { useProjectStore } from '../../store/projectStore';
import { useInventoryStore } from '../../store/inventoryStore';
import { useSitesStore } from '../../store/sitesStore';
import { getActedByName } from '../../store/authStore';
import { formatRecurrenceLabel } from '../../lib/jobRecurrence';
import JobStockRequestsSection from './JobStockRequestsSection';
import ChatPanel from '../common/ChatPanel';
import type { ChatContextType } from '../../lib/supabase';

interface Props {
  job?: Job | null;
  recurrenceRule?: JobRecurrenceRule | null;
  site: Site;
  onClose: () => void;
  onEdit: () => void;
  onEditSchedule?: () => void;
  onSave: (updated: Job) => void;
  onSiteClick?: (site: Site) => void;
  /** Render inside a parent drawer (no overlay / fixed positioning). */
  embedded?: boolean;
  /** When embedded, navigate back to the parent view (e.g. site details). */
  onBack?: () => void;
}

const fmtDate = (d: Date | undefined) => {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const fmtDateTime = (d: Date) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const HISTORY_LABELS: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  assignedTo: 'Assigned To',
  dueDate: 'Due Date',
  title: 'Title',
  description: 'Description',
};

const statusStyle = (status: JobStatus, isDark: boolean) => {
  switch (status) {
    case 'pending':     return isDark ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700';
    case 'in-progress': return isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700';
    case 'completed':   return isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700';
    case 'cancelled':   return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
    default: return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
  }
};

const priorityStyle = (priority: string | undefined, isDark: boolean) => {
  switch (priority) {
    case 'high':   return isDark ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700';
    case 'medium': return isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700';
    case 'low':    return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600';
    default: return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
  }
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const JobDrawer: React.FC<Props> = ({
  job,
  recurrenceRule,
  site,
  onClose,
  onEdit,
  onEditSchedule,
  onSave,
  onSiteClick,
  embedded = false,
  onBack,
}) => {
  const { appTheme } = useProjectStore();
  const { stockUnits, items } = useInventoryStore();
  const { sites } = useSitesStore();
  const isDark = appTheme === 'dark';

  const isScheduleOnly = !job && !!recurrenceRule;
  const isRecurringInstance = !!job?.isRecurringInstance || !!job?.recurrenceRuleId;
  const showScheduleInfo = isScheduleOnly || isRecurringInstance;
  const editRecurringSchedule = (isScheduleOnly || isRecurringInstance) && !!recurrenceRule && !!onEditSchedule;
  const canEditJob = !isRecurringInstance || !!recurrenceRule;

  const [visible, setVisible] = useState(embedded);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(true);
  const [completing, setCompleting] = useState(false);

  React.useEffect(() => {
    if (embedded) return;
    requestAnimationFrame(() => setVisible(true));
  }, [embedded]);
  const close = () => { setVisible(false); setTimeout(onClose, 220); };

  const drawerBg = isDark ? '#20242b' : '#ffffff';
  const border   = isDark ? '#343a43' : '#e5e7eb';
  const text     = isDark ? '#e5e7eb' : '#111827';
  const muted    = isDark ? '#9ca3af' : '#6b7280';
  const divider  = isDark ? '#2d3340' : '#f3f4f6';
  const inputBg  = isDark ? '#171a1f' : '#f9fafb';
  const rowHover = isDark ? '#262b35' : '#f9fafb';

  const displayTitle = job?.title ?? recurrenceRule?.title ?? 'Job';
  const displayDescription = job?.description ?? recurrenceRule?.description;
  const displayPriority = job?.priority ?? recurrenceRule?.priority;
  const displayAssignedTo = job?.assignedTo ?? recurrenceRule?.assignedTo;

  const instanceJobIds = useMemo(() => {
    if (!recurrenceRule) return job ? [job.id] : [];
    return (site.jobs ?? [])
      .filter((j) => j.recurrenceRuleId === recurrenceRule.id)
      .map((j) => j.id);
  }, [site.jobs, recurrenceRule, job]);

  const stockLinkJobId = job?.id
    ?? (site.jobs ?? []).find(
      (j) => j.recurrenceRuleId === recurrenceRule?.id
        && (j.status === 'pending' || j.status === 'in-progress'),
    )?.id
    ?? instanceJobIds[0]
    ?? null;

  const chatContextType: ChatContextType = job ? 'job' : 'recurrence_rule';
  const chatContextId = job?.id ?? recurrenceRule?.id ?? '';

  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex items-center justify-between gap-4 py-2" style={{ borderBottom: `1px solid ${divider}` }}>
      <span className="text-sm font-medium flex-shrink-0" style={{ color: text, minWidth: 110 }}>{label}</span>
      <span className="text-sm text-right" style={{ color: muted }}>{children}</span>
    </div>
  );

  const jobRef = `${displayTitle} · ${site.name}`;
  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);
  const allocatedUnits = useMemo(
    () => stockUnits.filter(u => u.reference === jobRef && u.status === 'checked out'),
    [stockUnits, jobRef],
  );

  const history: JobHistoryEntry[] = useMemo(() =>
    (job?.history ?? []).slice().sort((a, b) =>
      new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    ), [job?.history]);

  const handleComplete = () => {
    if (!job) return;
    setCompleting(true);
    const author = getActedByName() ?? 'Unknown';
    const entry: JobHistoryEntry = {
      id: generateId(),
      field: 'status',
      fromValue: job.status,
      toValue: 'completed',
      changedBy: author,
      changedAt: new Date(),
    };
    onSave({
      ...job,
      status: 'completed',
      updatedAt: new Date(),
      history: [...(job.history ?? []), entry],
    });
    setCompleting(false);
  };

  const headerSubtitle = isScheduleOnly
    ? `Recurring Schedule · ${site.name}`
    : `Job · ${site.name}`;

  const panel = (
      <div
        className={embedded ? 'flex flex-col h-full w-full' : 'fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl'}
        style={embedded ? undefined : {
          width: 400,
          backgroundColor: drawerBg,
          borderLeft: `1px solid ${border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}`, backgroundColor: embedded ? (isDark ? '#171a1f' : drawerBg) : drawerBg }}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {embedded && onBack && (
              <button
                onClick={onBack}
                className="p-1.5 rounded flex-shrink-0 transition-colors"
                style={{ color: muted }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                title="Back to site"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wide flex items-center gap-1" style={{ color: muted }}>
                {showScheduleInfo && <Repeat size={11} />}
                {headerSubtitle}
              </p>
              <h2 className="text-base font-semibold mt-1 truncate" style={{ color: text }}>{displayTitle}</h2>
              {isRecurringInstance && recurrenceRule && (
                <p className="text-xs mt-0.5 truncate" style={{ color: muted }}>
                  {formatRecurrenceLabel(recurrenceRule.intervalCount, recurrenceRule.intervalUnit)}
                  {' · '}Next: {fmtDate(recurrenceRule.nextRunAt)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {canEditJob && editRecurringSchedule && (
              <button
                onClick={onEditSchedule}
                className="px-3 py-1.5 text-sm rounded border transition-colors"
                style={{ borderColor: border, color: muted }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
              >
                Edit
              </button>
            )}
            {canEditJob && !editRecurringSchedule && (
              <button
                onClick={onEdit}
                className="px-3 py-1.5 text-sm rounded border transition-colors"
                style={{ borderColor: border, color: muted }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
              >
                Edit
              </button>
            )}
            {!embedded && (
              <button onClick={close} className="p-1.5 rounded hover:bg-gray-700/50 transition-colors" style={{ color: muted }}>
                <X size={16} />
              </button>
            )}
            {embedded && (
              <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-700/50 transition-colors" style={{ color: muted }} title="Close">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* Details */}
          <div className="px-5 pt-4 pb-2">
            <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: muted }}>Details</p>

            {isScheduleOnly && recurrenceRule && (
              <>
                <Row label="Type">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    recurrenceRule.isActive
                      ? (isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700')
                      : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                  }`}>
                    {recurrenceRule.isActive ? 'Active schedule' : 'Inactive schedule'}
                  </span>
                </Row>
                <Row label="Repeats">
                  {formatRecurrenceLabel(recurrenceRule.intervalCount, recurrenceRule.intervalUnit)}
                </Row>
                <Row label="Start Date">{fmtDate(recurrenceRule.startDate)}</Row>
                <Row label="End Date">{fmtDate(recurrenceRule.endDate)}</Row>
                <Row label="Next Run">{fmtDate(recurrenceRule.nextRunAt)}</Row>
                <Row label="Last Run">{fmtDate(recurrenceRule.lastRunAt)}</Row>
              </>
            )}

            {job && (
              <Row label="Status">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyle(job.status, isDark)}`}>
                  {job.status.replace('-', ' ')}
                </span>
              </Row>
            )}

            <Row label="Priority">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${priorityStyle(displayPriority, isDark)}`}>
                {displayPriority ?? '—'}
              </span>
            </Row>

            <Row label="Site">
              {onSiteClick ? (
                <button
                  type="button"
                  onClick={() => onSiteClick(site)}
                  className="text-sm underline-offset-2 hover:underline"
                  style={{ color: isDark ? '#93c5fd' : '#2563eb' }}
                >
                  {site.name}
                </button>
              ) : (
                site.name
              )}
            </Row>

            <Row label="Assigned To">{displayAssignedTo || <span style={{ color: muted }}>Unassigned</span>}</Row>

            {job && <Row label="Due Date">{fmtDate(job.dueDate)}</Row>}

            {job && <Row label="Created">{fmtDate(job.createdAt)}</Row>}
            {job && <Row label="Last Updated">{fmtDate(job.updatedAt)}</Row>}

            {isScheduleOnly && recurrenceRule && (
              <>
                <Row label="Created">{fmtDate(recurrenceRule.createdAt)}</Row>
                <Row label="Last Updated">{fmtDate(recurrenceRule.updatedAt)}</Row>
              </>
            )}

            {displayDescription && (
              <div className="py-2">
                <p className="text-sm font-medium mb-1" style={{ color: text }}>Description</p>
                <p className="text-sm leading-relaxed" style={{ color: muted }}>{displayDescription}</p>
              </div>
            )}

            {isScheduleOnly && (
              <p className="text-xs py-2 leading-relaxed" style={{ color: muted }}>
                Jobs are created automatically on each scheduled date. Once created, they work like any other job — you can complete them, link stock requests, check out equipment, and chat.
              </p>
            )}
          </div>

          {/* Complete button */}
          {job && job.status !== 'completed' && job.status !== 'cancelled' && (
            <div className="px-5 pb-2">
              <button
                onClick={handleComplete}
                disabled={completing}
                className="flex items-center gap-2 w-full justify-center py-2 rounded text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#16a34a', color: '#fff' }}
              >
                <CheckCircle size={15} />
                Mark as Completed
              </button>
              {isRecurringInstance && recurrenceRule && (
                <p className="text-xs text-center mt-1.5" style={{ color: muted }}>
                  Completing this job won&apos;t stop future occurrences. Next scheduled: {fmtDate(recurrenceRule.nextRunAt)}
                </p>
              )}
            </div>
          )}

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: border }} />

          {/* Allocated Equipment */}
          <div className="px-5 pb-2">
            <button
              type="button"
              onClick={() => setEquipmentOpen(o => !o)}
              className="flex items-center gap-1.5 w-full text-left mb-1 transition-colors"
              style={{ color: muted }}
            >
              <ChevronRight
                size={14}
                style={{ transition: 'transform 180ms', transform: equipmentOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
              />
              <span className="text-[11px] uppercase tracking-wide font-medium">
                Checked-Out Equipment {allocatedUnits.length > 0 && `(${allocatedUnits.length})`}
              </span>
            </button>

            {equipmentOpen && (
              allocatedUnits.length === 0 ? (
                <p className="text-sm pl-4 py-1" style={{ color: muted }}>No equipment checked out to this job yet.</p>
              ) : (
                <div className="pl-4">
                  {allocatedUnits.map((unit, idx) => {
                    const item = itemById.get(unit.itemId);
                    return (
                      <div
                        key={unit.id}
                        className="flex items-center justify-between gap-2 py-1"
                        style={{
                          borderBottom: idx < allocatedUnits.length - 1 ? `1px solid ${divider}` : undefined,
                        }}
                      >
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <span className="text-sm truncate" style={{ color: text }}>{item?.name ?? 'Unknown item'}</span>
                          {unit.serialMac && (
                            <span className="text-xs truncate flex-shrink-0" style={{ color: muted }}>{unit.serialMac}</span>
                          )}
                        </div>
                        <span
                          className="inline-flex items-center justify-center text-xs font-medium rounded px-1.5 py-0.5 flex-shrink-0"
                          style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#d1d5db' : '#374151' }}
                        >
                          #{unit.unitId}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>

          {stockLinkJobId ? (
            <JobStockRequestsSection
              job={job ?? {
                id: stockLinkJobId,
                title: displayTitle,
                status: 'pending',
                createdAt: new Date(),
                updatedAt: new Date(),
              }}
              site={site}
              sites={sites}
              isDark={isDark}
              text={text}
              muted={muted}
              border={border}
              divider={divider}
              relatedJobIds={instanceJobIds.length > 1 ? instanceJobIds : undefined}
            />
          ) : (
            <>
              <div className="mx-5 my-2" style={{ height: 1, backgroundColor: border }} />
              <div className="px-5 pb-2">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: muted }}>Stock Requests</p>
                <p className="text-sm py-1" style={{ color: muted }}>
                  Stock requests can be linked once the first job occurrence is created on {fmtDate(recurrenceRule?.nextRunAt)}.
                </p>
              </div>
            </>
          )}

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: border }} />

          {/* Chat */}
          {chatContextId && (
            <div className="px-5 pb-2">
              <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: muted }}>Chat</p>
              <div style={{ height: 280 }}>
                <ChatPanel
                  contextType={chatContextType}
                  contextId={chatContextId}
                  isDark={isDark}
                  theme={{ text, muted, border, inputBg, cardBg: drawerBg }}
                  emptyLabel="No messages yet."
                />
              </div>
            </div>
          )}

          {job && (
            <>
              <div className="mx-5 my-2" style={{ height: 1, backgroundColor: border }} />

              {/* History */}
              <div className="px-5 pb-5">
                <button
                  type="button"
                  onClick={() => setHistoryOpen(o => !o)}
                  className="flex items-center gap-1.5 w-full text-left mb-2 transition-colors"
                  style={{ color: muted }}
                >
                  <ChevronRight
                    size={14}
                    style={{ transition: 'transform 180ms', transform: historyOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  />
                  <span className="text-[11px] uppercase tracking-wide font-medium">
                    Change History {history.length > 0 && `(${history.length})`}
                  </span>
                </button>

                {historyOpen && (
                  history.length === 0 ? (
                    <p className="text-sm" style={{ color: muted }}>No changes recorded yet.</p>
                  ) : (
                    <div className="relative">
                      <div
                        className="absolute left-[6px] top-3 bottom-3 w-px"
                        style={{ backgroundColor: border }}
                      />
                      <div className="flex flex-col">
                        {history.map((entry, idx) => (
                          <div key={entry.id} className="relative pl-5 pb-4">
                            <div
                              className="absolute left-0 top-[5px] w-3 h-3 rounded-full border-2"
                              style={{
                                backgroundColor: drawerBg,
                                borderColor: idx === 0 ? (isDark ? '#60a5fa' : '#3b82f6') : (isDark ? '#4b5563' : '#d1d5db'),
                              }}
                            />
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium" style={{ color: text }}>
                                {HISTORY_LABELS[entry.field] ?? entry.field}
                              </span>
                              <span className="text-sm whitespace-nowrap flex-shrink-0" style={{ color: muted }}>
                                {fmtDateTime(new Date(entry.changedAt))}
                              </span>
                            </div>
                            <p className="text-sm leading-snug mt-0.5" style={{ color: muted }}>
                              {entry.fromValue
                                ? `"${entry.fromValue}" → "${entry.toValue}"`
                                : `set to "${entry.toValue}"`}
                            </p>
                            <p className="text-sm mt-0.5" style={{ color: muted }}>by {entry.changedBy}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </div>
  );

  if (embedded) return panel;

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />
      {panel}
    </>
  );
};

export default JobDrawer;
